/** @typedef {'closed' | 'autoDraft' | 'today' | 'default'} CalendarDayVariant */

/**
 * Parse YYYY-MM-DD as local calendar date (avoids UTC shift).
 * @param {string} isoDate
 * @returns {Date}
 */
export function parseLocalDate(isoDate) {
  const [year, month, day] = isoDate.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/**
 * @param {Date} date
 * @returns {string} YYYY-MM-DD
 */
export function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * @param {Date} date
 * @returns {string}
 */
export function formatDisplayDate(date) {
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * @param {Date} date
 * @returns {string}
 */
export function formatShortDate(date) {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

/**
 * @param {Date} start
 * @param {Date} end
 * @returns {Date[]}
 */
export function eachDayInRange(start, end) {
  const days = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  while (cursor <= last) {
    days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
}

/**
 * Week grid for a term: leading/trailing blanks only — never dates after endDate.
 * @param {Date} start
 * @param {Date} end
 * @returns {(Date | null)[]}
 */
export function buildTermCalendarCells(start, end) {
  const days = eachDayInRange(start, end);
  const leadingBlanks = start.getDay();
  const totalOccupied = leadingBlanks + days.length;
  const trailingBlanks = totalOccupied % 7 === 0 ? 0 : 7 - (totalOccupied % 7);

  return [
    ...Array.from({ length: leadingBlanks }, () => null),
    ...days,
    ...Array.from({ length: trailingBlanks }, () => null),
  ];
}

/**
 * @param {Date} a
 * @param {Date} b
 * @returns {boolean}
 */
export function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Strip time for comparisons.
 * @param {Date} date
 * @returns {Date}
 */
export function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * @param {Date} date
 * @returns {Date}
 */
export function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/**
 * @param {Date} date
 * @returns {Date}
 */
export function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

/**
 * @param {Date} date
 * @param {number} months
 * @returns {Date}
 */
export function addMonths(date, months) {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}
